import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  Alert,
  Divider,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import {
  Search,
  Visibility,
  Gavel,
  Warning,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  AttachMoney,
  Block,
  Message
} from '@mui/icons-material';
import axios from 'axios';

interface Dispute {
  id: string;
  rideId: string;
  reporterId: string;
  respondentId: string;
  type: string;
  status: string;
  description: string;
  reporterStatement?: string;
  respondentStatement?: string;
  resolution?: string;
  resolutionNotes?: string;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  reporter: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    profilePicture?: string;
  };
  respondent: {
    id: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
    email: string;
    profilePicture?: string;
  };
  ride: {
    id: string;
    departureTime: string;
    pricePerSeat: number;
    originCity: {
      name: string;
    };
    destinationCity: {
      name: string;
    };
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const DisputesPage: React.FC = () => {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalDisputes, setTotalDisputes] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('OPEN');
  const [filterType, setFilterType] = useState('ALL');
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [resolutionDialog, setResolutionDialog] = useState(false);
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info', message: string } | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inReview: 0,
    resolved: 0,
    escalated: 0
  });

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  const disputeTypes = [
    { value: 'FARE_DISAGREEMENT', label: 'Fare Disagreement' },
    { value: 'NO_SHOW_DRIVER', label: 'No-Show (Driver)' },
    { value: 'NO_SHOW_PASSENGER', label: 'No-Show (Passenger)' },
    { value: 'ROUTE_DEVIATION', label: 'Route Deviation' },
    { value: 'SERVICE_QUALITY', label: 'Service Quality' },
    { value: 'PAYMENT_ISSUE', label: 'Payment Issue' },
    { value: 'SAFETY_CONCERN', label: 'Safety Concern' },
    { value: 'OTHER', label: 'Other' }
  ];

  useEffect(() => {
    fetchDisputes();
    fetchStats();
  }, [page, rowsPerPage, search, filterStatus, filterType]);

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(search && { search }),
        ...(filterStatus !== 'ALL' && { status: filterStatus }),
        ...(filterType !== 'ALL' && { type: filterType })
      });

      const response = await axios.get(`${apiUrl}/admin/disputes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDisputes(response.data.disputes || []);
      setTotalDisputes(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      showAlert('error', 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const tasks = response.data.metrics?.adminTasks;
      setStats({
        total: tasks?.totalDisputes || 0,
        open: tasks?.openDisputes || 0,
        inReview: tasks?.disputesInReview || 0,
        resolved: tasks?.resolvedDisputes || 0,
        escalated: tasks?.escalatedDisputes || 0
      });
    } catch (error) {
      console.error('Failed to fetch dispute stats:', error);
    }
  };

  const handleViewDispute = (dispute: Dispute) => {
    setSelectedDispute(dispute);
    setDetailsDialog(true);
    setTabValue(0);
  };

  const handleOpenResolution = (type: string) => {
    setResolutionType(type);
    setResolutionDialog(true);
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute) return;

    if (!resolutionNotes.trim()) {
      showAlert('error', 'Please provide resolution notes');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const resolutionData: any = {
        resolution: resolutionType,
        resolutionNotes: resolutionNotes
      };

      if (resolutionType === 'REFUND' && refundAmount) {
        resolutionData.refundAmount = parseFloat(refundAmount);
      }

      if (resolutionType === 'CREDIT_ADJUSTMENT' && creditAmount) {
        resolutionData.creditAmount = parseFloat(creditAmount);
      }

      await axios.patch(
        `${apiUrl}/admin/disputes/${selectedDispute.id}/resolve`,
        resolutionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showAlert('success', 'Dispute resolved successfully');
      setResolutionDialog(false);
      setDetailsDialog(false);
      setResolutionType('');
      setResolutionNotes('');
      setRefundAmount('');
      setCreditAmount('');
      fetchDisputes();
      fetchStats();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Failed to resolve dispute');
    }
  };

  const showAlert = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'OPEN':
        return 'warning';
      case 'IN_REVIEW':
        return 'info';
      case 'RESOLVED':
        return 'success';
      case 'ESCALATED':
        return 'error';
      case 'CLOSED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getDisputeTypeLabel = (type: string) => {
    const disputeType = disputeTypes.find(t => t.value === type);
    return disputeType?.label || type.replace(/_/g, ' ');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSinceCreated = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffHours = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60));

    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Dispute Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and resolve conflicts between drivers and passengers
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchDisputes();
            fetchStats();
          }}
        >
          Refresh
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Stats Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Gavel color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {stats.total}
          </Typography>
          <Typography variant="body2" color="text.secondary">Total Disputes</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Warning color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {stats.open}
          </Typography>
          <Typography variant="body2" color="text.secondary">Open</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Pending color="info" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {stats.inReview}
          </Typography>
          <Typography variant="body2" color="text.secondary">In Review</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {stats.resolved}
          </Typography>
          <Typography variant="body2" color="text.secondary">Resolved</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Cancel color="error" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight="bold">
            {stats.escalated}
          </Typography>
          <Typography variant="body2" color="text.secondary">Escalated</Typography>
        </Paper>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by user, ride, or dispute ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="IN_REVIEW">In Review</MenuItem>
              <MenuItem value="RESOLVED">Resolved</MenuItem>
              <MenuItem value="ESCALATED">Escalated</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              {disputeTypes.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => {
            setSearch('');
            setFilterStatus('OPEN');
            setFilterType('ALL');
          }}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Disputes Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dispute ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Respondent</TableCell>
                <TableCell>Ride Route</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>SLA</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : disputes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No disputes found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                disputes.map((dispute) => {
                  const hoursSince = Math.floor((new Date().getTime() - new Date(dispute.createdAt).getTime()) / (1000 * 60 * 60));
                  const slaExceeded = hoursSince > 72 && dispute.status !== 'RESOLVED';

                  return (
                    <TableRow key={dispute.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {dispute.id.substring(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getDisputeTypeLabel(dispute.type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={dispute.reporter.profilePicture} sx={{ width: 32, height: 32 }}>
                            {dispute.reporter.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {dispute.reporter.preferredName || dispute.reporter.firstName} {dispute.reporter.lastName ? dispute.reporter.lastName.charAt(0) + '.' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={dispute.respondent.profilePicture} sx={{ width: 32, height: 32 }}>
                            {dispute.respondent.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {dispute.respondent.preferredName || dispute.respondent.firstName} {dispute.respondent.lastName ? dispute.respondent.lastName.charAt(0) + '.' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dispute.ride.originCity.name} → {dispute.ride.destinationCity.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={dispute.status.replace(/_/g, ' ')}
                          size="small"
                          color={getStatusColor(dispute.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {getTimeSinceCreated(dispute.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {slaExceeded ? (
                          <Chip label="SLA Exceeded" size="small" color="error" icon={<Warning />} />
                        ) : (
                          <Chip label={`${72 - hoursSince}h left`} size="small" color="success" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleViewDispute(dispute)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalDisputes}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Dispute Details Dialog */}
      <Dialog open={detailsDialog} onClose={() => setDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Dispute Details - {selectedDispute && getDisputeTypeLabel(selectedDispute.type)}
        </DialogTitle>
        <DialogContent>
          {selectedDispute && (
            <Box sx={{ pt: 2 }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label="Overview" />
                <Tab label="Statements" />
                <Tab label="Ride Details" />
              </Tabs>

              {/* Overview Tab */}
              <TabPanel value={tabValue} index={0}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Dispute Type</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {getDisputeTypeLabel(selectedDispute.type)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedDispute.status.replace(/_/g, ' ')}
                        color={getStatusColor(selectedDispute.status)}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{selectedDispute.description}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body1">{formatDateTime(selectedDispute.createdAt)}</Typography>
                  </Box>
                  {selectedDispute.resolvedAt && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Resolved</Typography>
                      <Typography variant="body1">{formatDateTime(selectedDispute.resolvedAt)}</Typography>
                    </Box>
                  )}
                  {selectedDispute.resolution && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Resolution</Typography>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{selectedDispute.resolution}</Typography>
                        {selectedDispute.resolutionNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>{selectedDispute.resolutionNotes}</Typography>
                        )}
                      </Alert>
                    </Box>
                  )}
                </Stack>
              </TabPanel>

              {/* Statements Tab */}
              <TabPanel value={tabValue} index={1}>
                <Stack spacing={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar src={selectedDispute.reporter.profilePicture}>
                          {selectedDispute.reporter.firstName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Reporter: {selectedDispute.reporter.preferredName || selectedDispute.reporter.firstName} {selectedDispute.reporter.lastName ? selectedDispute.reporter.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedDispute.reporter.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">
                        {selectedDispute.reporterStatement || selectedDispute.description}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar src={selectedDispute.respondent.profilePicture}>
                          {selectedDispute.respondent.firstName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            Respondent: {selectedDispute.respondent.preferredName || selectedDispute.respondent.firstName} {selectedDispute.respondent.lastName ? selectedDispute.respondent.lastName.charAt(0) + '.' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedDispute.respondent.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">
                        {selectedDispute.respondentStatement || 'No response provided yet'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </TabPanel>

              {/* Ride Details Tab */}
              <TabPanel value={tabValue} index={2}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Route</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedDispute.ride.originCity.name} → {selectedDispute.ride.destinationCity.name}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Departure Time</Typography>
                    <Typography variant="body1">{formatDateTime(selectedDispute.ride.departureTime)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Price Per Seat</Typography>
                    <Typography variant="body1">${selectedDispute.ride.pricePerSeat}</Typography>
                  </Box>
                </Stack>
              </TabPanel>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedDispute && selectedDispute.status !== 'RESOLVED' && selectedDispute.status !== 'CLOSED' && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => handleOpenResolution('REFUND')}
                color="success"
                variant="outlined"
                startIcon={<AttachMoney />}
              >
                Issue Refund
              </Button>
              <Button
                onClick={() => handleOpenResolution('CREDIT_ADJUSTMENT')}
                color="info"
                variant="outlined"
                startIcon={<AttachMoney />}
              >
                Adjust Credits
              </Button>
              <Button
                onClick={() => handleOpenResolution('WARNING')}
                color="warning"
                variant="outlined"
                startIcon={<Warning />}
              >
                Issue Warning
              </Button>
              <Button
                onClick={() => handleOpenResolution('SUSPEND_USER')}
                color="error"
                variant="outlined"
                startIcon={<Block />}
              >
                Suspend User
              </Button>
              <Button
                onClick={() => handleOpenResolution('CLOSE_WITH_NOTES')}
                variant="outlined"
                startIcon={<Message />}
              >
                Close
              </Button>
            </Box>
          )}
          <Button onClick={() => setDetailsDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Resolution Dialog */}
      <Dialog open={resolutionDialog} onClose={() => setResolutionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Resolve Dispute - {resolutionType.replace(/_/g, ' ')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 2 }}>
            {resolutionType === 'REFUND' && (
              <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            )}
            {resolutionType === 'CREDIT_ADJUSTMENT' && (
              <TextField
                fullWidth
                label="Credit Amount"
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            )}
            <TextField
              fullWidth
              label="Resolution Notes"
              multiline
              rows={4}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Explain the resolution and actions taken..."
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleResolveDispute}
            variant="contained"
            color="primary"
            disabled={!resolutionNotes.trim()}
          >
            Confirm Resolution
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisputesPage;
